import { useEffect, useRef, useState } from "react";
import type { IntercomConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";

/**
 * Door-entry video intercom (the "speaker phone" at the entrance). A visitor
 * buzzes; their live face + spoken pretext appear under a CCTV flicker. The
 * learner can buzz them in (the demonstrated mistake) or try to verify first —
 * which only triggers more social pressure, mirroring how confident impersonators
 * talk their way in.
 */
export function Intercom({
  config,
  onOpenDoor,
  onVerify,
}: {
  config: IntercomConfig;
  onOpenDoor: () => void;
  /** Safe exit: the learner stays firm after the pressure line. */
  onVerify?: () => void;
}) {
  const [pressured, setPressured] = useState(false);
  const started = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // The clip currently in the feed: entry line, then the pressure line if the
  // learner stalls. Same avatar + voice, so the face never freezes and the
  // voice never changes between the two.
  const currentClip =
    pressured && config.clipPressureUrl ? config.clipPressureUrl : config.clipUrl;

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    {
      const vopts = { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" };
      const entryLine = config.speak ?? config.message;
      if (!config.clipUrl && entryLine) void audio.prefetchSpeech(entryLine, vopts);
      if (!config.clipPressureUrl && config.pressure) void audio.prefetchSpeech(config.pressure, vopts);
    }
    if (started.current) return;
    started.current = true;
    audio.buzz();
    // If we have a talking-head clip, its baked voice plays — do NOT also TTS
    // (that was the double-voice). The clip starts after the buzz.
    if (config.clipUrl) {
      window.setTimeout(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !audio.isEnabled();
        v.play().catch(() => {});
      }, 850);
    } else if (config.speak ?? config.message) {
      const line = config.speak ?? config.message;
      window.setTimeout(() => {
        audio.speak(line, { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" });
      }, 900);
    }
    return () => audio.cancelSpeech();
  }, [config]);

  // When the learner stalls, play the pressure clip (movement + same voice). If
  // there is no pressure clip, fall back to TTS of the pressure line.
  useEffect(() => {
    if (!pressured) return;
    const v = videoRef.current;
    if (config.clipPressureUrl && v) {
      v.muted = !audio.isEnabled();
      try {
        v.load();
      } catch {
        /* ignore */
      }
      v.play().catch(() => {});
    } else if (config.pressure) {
      if (v) {
        try {
          v.pause();
        } catch {
          /* ignore */
        }
      }
      audio.speak(config.pressure, { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" });
    }
  }, [pressured, config]);

  const initials =
    config.visitorInitials ??
    config.visitorName
      .split(/\s+/)
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className="intercom">
      <div className="ic-station">
        <div className="ic-head">
          <span className="ic-dot" aria-hidden />
          <span className="ic-title">{config.station ?? "Main Entrance"}</span>
          <span className="ic-live">LIVE</span>
        </div>

        {/* visitor video feed (CCTV) */}
        <div className="ic-feed">
          {config.clipUrl ? (
            <video
              ref={videoRef}
              className="ic-video"
              src={currentClip}
              playsInline
              preload="auto"
              onError={() => {
                // Clip missing/blocked -> still voice the line via TTS.
                audio.speak(pressured ? config.pressure ?? config.message : config.speak ?? config.message, {
                  voice: config.voiceId ?? "onyx",
                  lang: config.language ?? "en-US",
                });
              }}
            />
          ) : (
            <>
              <div className="ic-feed-noise" aria-hidden />
              <div className="ic-avatar">{initials}</div>
            </>
          )}
          {/* CCTV overlays: scanlines, rolling band, flicker, vignette */}
          <div className="ic-cctv" aria-hidden>
            <div className="ic-scan" />
            <div className="ic-roll" />
            <div className="ic-flicker" />
            <div className="ic-vig" />
          </div>
          <div className="ic-feed-meta">
            <span className="ic-rec" aria-hidden />
            CAM 1 · ENTRANCE
          </div>
          <div className="ic-ring">Doorbell · ringing…</div>
        </div>

        {/* what the visitor says */}
        <div className="ic-caption">
          <div className="ic-who">
            <strong>{config.visitorName}</strong>
            {config.visitorOrg && <span className="ic-org">{config.visitorOrg}</span>}
          </div>
          <p className="ic-msg">“{config.message}”</p>
          {config.subtext && <p className="ic-sub">{config.subtext}</p>}
          {pressured && config.pressure && (
            <p className="ic-pressure">“{config.pressure}”</p>
          )}
        </div>

        <div className="ic-actions">
          <ChoiceList>
            {!pressured && config.verifyLabel && (
              <ChoiceCard
                category="verify"
                reply={config.verifyLabel}
                onClick={() => setPressured(true)}
              />
            )}
            {pressured && onVerify && (
              <ChoiceCard
                category="verify"
                reply={config.holdLabel ?? "Stay firm: call HQ and check first"}
                onClick={() => {
                  audio.cancelSpeech();
                  videoRef.current?.pause();
                  onVerify();
                }}
              />
            )}
            <ChoiceCard
              category="comply"
              reply={config.openLabel ?? "Open the door"}
              onClick={onOpenDoor}
            />
          </ChoiceList>
        </div>
      </div>
    </div>
  );
}
